import { STATUS_NO_CONTENT } from 'constants/http';

import { callWithoutContext } from './callstack';

// behavior the queue:
//
// 1. automatically begins uploads on first job of an empty queue
// 2. automatically begins next job if the foremost attachment job is removed
// 3. it will *not* go to next queued upload on success of a job (up to the
//    caller in `onSuccess`)
// 4. will pause on failure of a job and must be retried via `runUploadQueue`
// 5. if you wish to skip an errored or ongoing upload, call `removeFromUploadQueue`
//    with the same attachmentId
//
// each job takes the shape: {
//   attachment,
//   file,
//   credentials,
//   key,
//   onError,
//   onProgress,
//   onSuccess,
//   onStart,
// }
let attachmentUploadQueue = [];
let running = false;
let paused = false;

// for cancellation purposes
let currentUploadXHR = null;
let currentJob = null;

export const queueUploadAttachment = ({
  attachment,
  file,
  credentials,
  key,
  mime,
  onError = () => {},
  onProgress = () => {},
  onSuccess = () => {},
  onStart = () => {},
}) => {
  attachmentUploadQueue.push({
    attachment,
    file,
    credentials,
    key,
    mime,
    onError,
    onProgress,
    onSuccess,
    onStart,
  });

  if (!running && !paused) {
    runUploadQueue();
  }
};

export const removeFromUploadQueue = attachmentId => {
  const jobIndex = attachmentUploadQueue.indexOf(
    job => job.attachment.id === attachmentId
  );

  attachmentUploadQueue = attachmentUploadQueue.filter(
    job => job.attachment.id !== attachmentId
  );

  if (currentJob && currentJob.attachment.id === attachmentId) {
    currentUploadXHR.abort();
    currentJob = null;
    currentUploadXHR = null;
  }

  if (jobIndex === 0) {
    runUploadQueue();
  }
};

export const runUploadQueue = () => {
  // already running
  if (currentJob || running) {
    return;
  }

  running = true;
  paused = false;

  // clear call stack, so we don't recursively grow it
  callWithoutContext(runNextJob);
};

async function runNextJob() {
  running = true;
  paused = false;

  const nextJob = attachmentUploadQueue.shift();

  // done
  if (!nextJob) {
    running = false;

    return;
  }

  currentJob = nextJob;

  const {
    attachment,
    file,
    credentials,
    onError,
    onProgress,
    onSuccess,
    onStart,
  } = nextJob;

  try {
    onStart();

    const { url, fields } = credentials;

    const formData = new FormData();

    Object.keys(fields).forEach(key => {
      formData.append(key, fields[key]);
    });

    // Actual file has to be appended last.
    formData.append('file', file);

    await new Promise((resolve, reject) => {
      currentUploadXHR = new XMLHttpRequest();

      currentUploadXHR.onload = function () {
        this.status === STATUS_NO_CONTENT
          ? resolve()
          : reject(this.responseText);
      };
      currentUploadXHR.upload.onprogress = function (e) {
        onProgress(e.loaded / e.total);
      };

      currentUploadXHR.open('POST', url, true);
      currentUploadXHR.send(formData);
    });

    running = false;
    paused = false;
    currentUploadXHR = null;
    currentJob = null;

    attachmentUploadQueue = attachmentUploadQueue.filter(
      job => job.attachment.id !== attachment.id
    );

    onSuccess();
  } catch (err) {
    // halted state
    running = false;
    paused = true;
    currentUploadXHR = null;
    currentJob = null;

    // re-queue to front in case we want to retry
    attachmentUploadQueue.unshift(nextJob);
    onError(err);
  }
}
